import React, { useState, useEffect } from 'react';
import {
  ChevronRight,
  ChevronLeft,
  Package,
  Clock,
  TruckIcon,
  X,
  Loader2
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';

interface RecentShipment {
  id: string;
  shipment_number: string;
  supplier_name: string;
  received_date: string;
  status: string;
  item_count: number;
  total_quantity: number;
}

interface RecentActivitySidebarProps {
  adminId: string;
}

export const RecentActivitySidebar: React.FC<RecentActivitySidebarProps> = ({ adminId }) => {
  const [isOpen, setIsOpen] = useState(() => {
    const stored = localStorage.getItem('recentActivitySidebarOpen');
    return stored === 'true';
  });
  const [shipments, setShipments] = useState<RecentShipment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    localStorage.setItem('recentActivitySidebarOpen', String(isOpen));
  }, [isOpen]);

  useEffect(() => {
    if (adminId) {
      fetchRecentShipments();
    }
  }, [adminId]);

  const fetchRecentShipments = async () => {
    try {
      setLoading(true);
      const { data: shipmentsData, error } = await supabase
        .from('incoming_shipments')
        .select(`
          id,
          shipment_number,
          supplier_name,
          received_date,
          status
        `)
        .eq('admin_id', adminId)
        .order('received_date', { ascending: false })
        .limit(5);

      if (error) throw error;

      if (shipmentsData) {
        const shipmentsWithItems = await Promise.all(
          shipmentsData.map(async (shipment) => {
            const { data: items } = await supabase
              .from('shipment_items')
              .select('received_quantity')
              .eq('shipment_id', shipment.id);

            const itemCount = items?.length || 0;
            const totalQuantity = items?.reduce((sum, item) => sum + (item.received_quantity || 0), 0) || 0;

            return {
              ...shipment,
              item_count: itemCount,
              total_quantity: totalQuantity
            };
          })
        );
        setShipments(shipmentsWithItems);
      }
    } catch (err) {
      console.error('Error fetching recent shipments:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, h:mm a');
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'received':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
      case 'pending':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center gap-1 px-2 py-4 bg-teal-600 hover:bg-teal-700 text-white rounded-l-lg shadow-lg transition-all duration-300 ${
          isOpen ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
        }`}
        title="Recent Activity"
      >
        <Clock className="h-4 w-4" />
        <ChevronLeft className="h-4 w-4" />
      </button>

      <div
        className={`fixed inset-y-0 right-0 z-50 w-full sm:w-80 bg-white dark:bg-gray-800 shadow-2xl transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                <Clock className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Recent Activity</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Last 5 shipments received</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 text-teal-600 animate-spin mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading recent activity...</p>
              </div>
            ) : shipments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <TruckIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">No recent shipments</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Completed shipments will appear here
                </p>
              </div>
            ) : (
              shipments.map((shipment, index) => (
                <div
                  key={shipment.id}
                  className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-teal-300 dark:hover:border-teal-600 transition-colors"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                      <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                        {shipment.shipment_number}
                      </span>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(shipment.status)}`}>
                      {shipment.status}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Supplier</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[140px]">
                        {shipment.supplier_name}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Items</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {shipment.item_count} items
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Total Qty</span>
                      <span className="font-semibold text-green-600 dark:text-green-400">
                        +{shipment.total_quantity}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <Clock className="h-3 w-3 mr-1" />
                    {formatDate(shipment.received_date)}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <button
              onClick={() => setIsOpen(false)}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
              Close Panel
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 dark:bg-black/40 z-40 sm:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};
